import { type CSSProperties, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGetBlogPosts } from '@/services/queries/blog';
import {
	Check,
	Flame,
	Mail,
	Maximize2,
	Phone,
	Sparkles,
} from 'lucide-react';
import { MediaLightbox } from '@/pages/public/landing/components/MediaLightbox';
import { ReclameAquiVerifiedSection } from '@/pages/public/business-landing/components/ReclameAquiVerifiedSection';
import {
	WHATSAPP_LINK_P2P,
	WHATSAPP_NUMBER_FORMATTED,
} from '@/constants/whatsapp';
import { PsychologistComparisonTable } from './components/PsychologistComparisonTable';
import { WorkflowDemoPlayer } from './components/demos/WorkflowDemoPlayer';
import {
	isWorkflowAnimatedDemoId,
	type WorkflowAnimatedDemoId,
} from './components/demos/workflowDemoConstants';
import { PsychologistLandingFaqSection } from './PsychologistLandingFaqSection';
import {
	formatPsychologistPrice,
	P2P_PLAN,
	LANDING_SOLUTIONS,
	PARTNER_INTEGRATIONS,
	TRUST_ITEMS,
	WORKFLOW_DEMOS,
} from './psychologistLandingData';
import { cn } from '@/lib/utils';

const PLUMAA_LOGO_WHITE =
	'https://res.cloudinary.com/dd5pbp2tb/image/upload/v1770986600/Plumaa_H_W_hribid.png';

const DASHED_PATH_STYLE: CSSProperties = {
	strokeDasharray: 12,
	strokeDashoffset: 1000,
	animation: 'plumaaPsychDash 40s linear infinite',
};

const trustAccentClasses = {
	orange: 'border-brand-orange/10 text-brand-orange',
	pink: 'border-brand-pink/10 text-brand-pink',
	blue: 'border-brand-blue/10 text-brand-blue',
};

function WorkflowMediaPlaceholder({ embedded = false }: { embedded?: boolean }) {
	return (
		<div
			className={cn(
				'flex aspect-[16/10] w-full items-center justify-center bg-gradient-to-br from-brand-orange/5 to-brand-pink/5 px-6 py-12 text-center',
				embedded
					? 'h-full rounded-none border-0 shadow-none'
					: 'rounded-2xl border border-brand-orange/10 shadow-sm',
			)}
		>
			<p className="text-sm font-light text-gray-400">
				Demonstração em breve
			</p>
		</div>
	);
}

export function PsychologistLandingBody() {
	const navigate = useNavigate();
	const [lightboxMedia, setLightboxMedia] = useState<{
		src: string;
		alt: string;
	} | null>(null);
	const [expandedAnimatedDemo, setExpandedAnimatedDemo] = useState<{
		demoId: WorkflowAnimatedDemoId;
		alt: string;
	} | null>(null);

	const { data: blogData } = useGetBlogPosts({
		page: 1,
		limit: 3,
		sort: 'created_at',
		sort_direction: 'desc',
	});

	const scrollToPricing = () => {
		const element = document.getElementById('planos');
		if (element) {
			const offset = 40;
			const bodyRect = document.body.getBoundingClientRect().top;
			const elementRect = element.getBoundingClientRect().top;
			const elementPosition = elementRect - bodyRect;
			window.scrollTo({
				top: elementPosition - offset,
				behavior: 'smooth',
			});
		}
	};

	const goToRegister = () => navigate('/profissional/cadastro');

	const renderWorkflowMedia = (
		demo: (typeof WORKFLOW_DEMOS)[number],
		embedded = false,
	) => {
		const embeddedMediaClass = embedded
			? 'h-full w-full rounded-none border-0 shadow-none'
			: '';

		if (isWorkflowAnimatedDemoId(demo.id)) {
			const animatedDemoId = demo.id;

			return (
				<WorkflowDemoPlayer
					demoId={animatedDemoId}
					alt={demo.alt}
					onExpand={() =>
						setExpandedAnimatedDemo({
							demoId: animatedDemoId,
							alt: demo.alt,
						})
					}
					className={embeddedMediaClass}
				/>
			);
		}

		if (!demo.src) {
			return <WorkflowMediaPlaceholder embedded={embedded} />;
		}

		return (
			<button
				type="button"
				onClick={() =>
					setLightboxMedia({
						src: demo.src as string,
						alt: demo.alt,
					})
				}
				aria-label={`Ampliar demonstração: ${demo.title}`}
				className={cn(
					'group/media relative block aspect-[16/10] w-full cursor-pointer overflow-hidden bg-white p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2',
					embedded
						? 'h-full rounded-none border-0 shadow-none'
						: 'rounded-2xl border border-brand-orange/10 shadow-lg shadow-brand-orange/5',
				)}
			>
				<img
					src={demo.src}
					alt={demo.alt}
					className="block h-full w-full object-cover"
				/>
				<div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover/media:bg-black/40 group-hover/media:opacity-100 group-focus-visible/media:bg-black/40 group-focus-visible/media:opacity-100">
					<Maximize2
						className="h-10 w-10 text-white drop-shadow"
						strokeWidth={2.25}
						aria-hidden
					/>
				</div>
			</button>
		);
	};

	const renderWorkflowMediaBlock = (
		demo: (typeof WORKFLOW_DEMOS)[number],
	) => (
		<div className="overflow-hidden rounded-2xl border border-brand-orange/10 shadow-sm transition-all duration-300 group-hover:border-brand-orange/25 group-hover:shadow-lg group-hover:shadow-brand-orange/10">
			<div className="relative aspect-[16/10] overflow-hidden">
				<div className="size-full transition-transform duration-500 ease-out group-hover:scale-105">
					{renderWorkflowMedia(demo, true)}
				</div>
				<div className="pointer-events-none absolute inset-0 z-20 bg-gradient-to-t from-[#3d3431]/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
			</div>
		</div>
	);

	const renderWorkflowCopy = (
		demo: (typeof WORKFLOW_DEMOS)[number],
		mediaOnLeft: boolean,
	) => (
		<div
			className={cn(
				'px-2 lg:px-4',
				mediaOnLeft ? 'text-left' : 'text-left lg:text-right',
			)}
		>
			<p className="mb-3 text-[13px] font-bold uppercase tracking-[0.12em] text-brand-orange/70 transition-colors duration-300 group-hover:text-brand-orange">
				{demo.eyebrow}
			</p>
			<h3 className="mb-3 text-[28px] font-bold leading-[1.2] tracking-tight text-zinc-500 transition-colors duration-300 group-hover:text-[#3d3431] md:text-[30px]">
				{demo.title}{' '}
				<span className="text-zinc-500 transition-colors duration-300 group-hover:text-brand-orange">
					{demo.titleAccent}
				</span>
			</h3>
			<p className="mb-4 text-[15px] font-light leading-[1.75] text-zinc-400 transition-colors duration-300 group-hover:text-gray-500">
				{demo.description}
			</p>
			<span className="inline-block rounded-md border border-brand-orange/15 bg-brand-orange/5 px-3 py-1.5 text-xs font-semibold text-brand-orange/70 transition-colors duration-300 group-hover:border-brand-orange/20 group-hover:bg-brand-orange/10 group-hover:text-brand-orange">
				{demo.tag}
			</span>
		</div>
	);

	return (
		<>
			<style>{`@keyframes plumaaPsychDash { to { stroke-dashoffset: 0; } }`}</style>
			<div className="min-h-screen bg-[#fffcfb] antialiased [background-image:radial-gradient(circle,rgba(255,113,70,0.04)_1px,transparent_1px)] [background-size:40px_40px]">
				<section
					id="hero"
					className="relative overflow-hidden border-b border-brand-orange/10 pb-32 pt-12"
				>
					<div className="pointer-events-none absolute inset-0 z-0 hidden lg:block">
						<svg
							className="h-full min-h-[600px] w-full"
							viewBox="0 0 1440 800"
							preserveAspectRatio="none"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M1020,-50 C1020,150 1280,100 1280,250 C1280,380 1100,380 1100,250 C1100,180 1180,180 1220,250 C1260,350 1220,650 1350,720 C1450,780 1550,680 1700,680"
								stroke="#ff7146"
								strokeWidth="2.5"
								strokeLinecap="round"
								opacity="0.45"
								style={DASHED_PATH_STYLE}
							/>
						</svg>
					</div>

					<div className="relative z-10 mx-auto mt-16 max-w-6xl px-6 text-center">
						<div className="mb-4 inline-flex items-center gap-2 rounded-full border border-brand-orange/20 bg-brand-orange/5 px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-brand-orange">
							<Sparkles className="h-3.5 w-3.5" />
							Consultório digital com IA
						</div>

						<h1 className="mb-5 text-4xl font-extrabold leading-[1.1] tracking-tight text-[#3d3431] md:text-6xl">
							<span className="block text-3xl font-bold md:text-5xl">
								Seu consultório completo.
							</span>
							<span className="mt-2 block text-brand-orange transition-all duration-300 hover:text-brand-pink md:text-6xl">
								Prontuário evoluído por IA.
							</span>
						</h1>

						<p className="mb-3 text-2xl font-medium text-[#3d3431]/85">
							<strong className="text-3xl font-extrabold text-brand-orange">
								7 dias grátis
							</strong>
						</p>

						<p className="mx-auto mb-10 max-w-2xl font-sans text-lg font-light leading-relaxed text-gray-500 md:text-xl">
							Economize horas por semana em documentação, agenda e
							lembretes. A Plumaa automatiza o que trava sua rotina
							para você atender com mais agilidade e menos
							burocracia.
						</p>

						<div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
							<button
								type="button"
								onClick={goToRegister}
								className="w-full cursor-pointer rounded-2xl bg-gradient-to-r from-brand-orange to-[#ffcd46] px-10 py-4 text-lg font-bold text-white shadow-xl shadow-brand-orange/25 transition-all hover:scale-[1.02] active:scale-95 sm:w-auto"
							>
								Começar teste grátis
							</button>
							<button
								type="button"
								onClick={scrollToPricing}
								className="w-full cursor-pointer rounded-2xl border border-brand-orange/30 bg-white px-10 py-4 text-lg font-bold text-brand-orange transition-all hover:bg-brand-orange/5 sm:w-auto"
							>
								Ver plano
							</button>
						</div>
					</div>
				</section>

				<section
					id="solucoes"
					className="border-b border-brand-orange/10 bg-white py-24"
				>
					<div className="mx-auto max-w-6xl px-6">
						<div className="mb-16 space-y-3 text-center">
							<p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
								Nossas soluções
							</p>
							<h2 className="text-4xl font-bold text-[#3d3431]">
								Tudo que você precisa para{' '}
								<span className="text-brand-orange">
									atender com tranquilidade.
								</span>
							</h2>
							<p className="mx-auto max-w-xl text-lg font-light text-gray-400">
								A Plumaa foi feita para psicólogos que querem
								priorizar o atendimento, não a burocracia.
							</p>
						</div>

						<div className="space-y-24 lg:space-y-32">
							{LANDING_SOLUTIONS.map((item, index) => {
								const imageOnLeft = index % 2 === 0;
								const imageBlock = (
									<div className="overflow-hidden rounded-2xl border border-brand-orange/10 shadow-sm transition-all duration-300 group-hover:border-brand-orange/25 group-hover:shadow-lg group-hover:shadow-brand-orange/10">
										<div className="relative aspect-[16/10] overflow-hidden">
											<img
												src={item.image}
												alt={item.title}
												className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
											/>
											<div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#3d3431]/20 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
										</div>
									</div>
								);
								const textBlock = (
									<div
										className={cn(
											'px-2 lg:px-4',
											imageOnLeft
												? 'text-left'
												: 'text-left lg:text-right',
										)}
									>
										<h3 className="mb-4 text-2xl font-bold text-zinc-500 transition-colors duration-300 group-hover:text-[#3d3431] md:text-3xl">
											{item.title}
										</h3>
										<p className="text-base font-light leading-relaxed text-zinc-400 transition-colors duration-300 group-hover:text-gray-500 md:text-lg">
											{item.description}
										</p>
									</div>
								);

								return (
									<div
										key={item.id}
										className="group grid w-full items-center gap-5 lg:grid-cols-2 lg:gap-8"
									>
										<div
											className={cn(
												imageOnLeft
													? ''
													: 'order-2 lg:order-1',
											)}
										>
											{imageOnLeft ? imageBlock : textBlock}
										</div>
										<div
											className={cn(
												imageOnLeft
													? ''
													: 'order-1 lg:order-2',
											)}
										>
											{imageOnLeft ? textBlock : imageBlock}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</section>

				<section
					id="como-funciona"
					className="border-b border-brand-orange/10 bg-[#fffcfb] py-24"
				>
					<div className="mx-auto max-w-6xl px-6">
						<div className="mb-20 text-center">
							<p className="mb-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
								Seu dia a dia na clínica
							</p>
							<h2 className="mb-4 text-4xl font-extrabold leading-[1.12] text-[#3d3431] md:text-5xl">
								Veja como acontece
								<br />
								<span className="text-brand-orange">
									do lado de cá.
								</span>
							</h2>
							<p className="mx-auto max-w-2xl font-light leading-relaxed text-gray-500">
								Do agendamento ao prontuário pronto — o fluxo
								completo do psicólogo dentro da Plumaa.
							</p>
						</div>

						<div className="space-y-24 lg:space-y-32">
							{WORKFLOW_DEMOS.map((demo) => {
								const mediaOnLeft = Boolean(demo.mediaFirst);
								const mediaBlock = renderWorkflowMediaBlock(demo);
								const copyBlock = renderWorkflowCopy(demo, mediaOnLeft);

								return (
									<div
										key={demo.id}
										className="group grid w-full items-center gap-5 lg:grid-cols-2 lg:gap-8"
									>
										<div className="order-2 lg:order-1">
											{mediaOnLeft ? mediaBlock : copyBlock}
										</div>
										<div className="order-1 lg:order-2">
											{mediaOnLeft ? copyBlock : mediaBlock}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</section>

				<section
					id="comparativo"
					className="border-b border-brand-orange/10 bg-white py-24"
				>
					<div className="mx-auto max-w-6xl px-6">
						<div className="mb-20 space-y-3 text-center">
							<p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
								Valor que faz sentido
							</p>
							<h2 className="text-4xl font-extrabold leading-[1.12] text-[#3d3431] md:text-5xl">
								Tudo o que você viu{' '}
								<span className="text-brand-orange">
									já está incluso.
								</span>
							</h2>
							<p className="mx-auto max-w-2xl text-lg font-light leading-relaxed text-gray-500">
								Do prontuário com IA aos lembretes no WhatsApp —
								tudo que você viu até aqui já está incluso. Sem
								módulos extras nem surpresas na fatura.
							</p>
						</div>

						<div className="mx-auto max-w-5xl">
							<PsychologistComparisonTable />
						</div>
					</div>
				</section>

				<section
					id="planos"
					className="relative border-b border-brand-orange/10 bg-[#fffcfb] py-24"
				>
					<div className="mx-auto max-w-5xl px-6">
						<div className="mb-16 space-y-3 text-center">
							<p className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange">
								Plano único
							</p>
							<h2 className="text-4xl font-extrabold leading-[1.12] text-[#3d3431] md:text-5xl">
								Um plano.{' '}
								<span className="text-brand-orange">
									Tudo incluso.
								</span>
							</h2>
							<p className="mx-auto max-w-2xl text-lg font-light leading-relaxed text-gray-500">
								Sem taxas extras nem burocracia. Um preço justo
								para o consultório completo que você acabou de
								conhecer.
							</p>
						</div>

						<div className="overflow-hidden rounded-2xl border-2 border-brand-orange bg-[#fff9f7] shadow-xl shadow-brand-orange/10 transition-all duration-300 hover:border-brand-orange/80 hover:shadow-2xl hover:shadow-brand-orange/15">
							<div className="grid lg:grid-cols-2">
								<div className="flex flex-col border-b border-brand-orange/10 p-8 lg:border-b-0 lg:border-r lg:p-10">
									<p className="mb-6 text-xs font-bold uppercase tracking-[0.18em] text-gray-400">
										O que está incluso
									</p>
									<ul className="flex flex-1 flex-col gap-4">
										{P2P_PLAN.features.map((feat) => (
											<li
												key={feat.text}
												className={cn(
													'flex items-start gap-2.5 text-sm',
													feat.highlight
														? 'font-albert text-[15px] font-semibold leading-snug text-[#3d3431]'
														: 'text-gray-500',
												)}
											>
												<Check
													className={cn(
														'mt-0.5 h-4 w-4 shrink-0',
														feat.highlight
															? 'text-brand-orange'
															: 'text-brand-orange/70',
													)}
												/>
												<span className="leading-tight">
													{feat.text}
												</span>
											</li>
										))}
									</ul>
								</div>

								<div className="flex flex-col justify-center p-8 lg:p-10">
									<div className="mb-1 flex items-center justify-between gap-4">
										<h3 className="font-albert text-xl font-bold leading-tight text-[#3d3431]">
											{P2P_PLAN.name}
										</h3>
										<span className="flex shrink-0 items-center gap-1 rounded-full bg-gradient-to-r from-brand-orange to-brand-pink px-4 py-1.5 text-[9px] font-bold uppercase tracking-[0.15em] text-white shadow-md">
											<Flame className="h-4 w-4" />
											Ideal para você
										</span>
									</div>
									<p className="font-albert mb-6 text-xs font-light italic text-gray-400">
										{P2P_PLAN.trialDays} dias grátis
									</p>

									<div className="mb-8 text-center">
										<p className="font-albert mb-3 text-sm font-medium text-gray-500 md:text-base">
											1º mês por apenas
										</p>
										<p className="font-albert text-4xl font-extrabold leading-none text-[#3d3431] md:text-5xl">
											R${' '}
											{formatPsychologistPrice(
												P2P_PLAN.firstMonthPrice,
											)}
										</p>
										<p className="font-albert mt-3 text-sm text-gray-400">
											depois R${' '}
											{formatPsychologistPrice(
												P2P_PLAN.price,
											)}
											/mês
										</p>
									</div>

									<button
										type="button"
										onClick={goToRegister}
										className="w-full cursor-pointer rounded-xl bg-gradient-to-r from-brand-orange to-brand-pink py-3.5 text-sm font-bold text-white shadow-md shadow-brand-orange/20 transition-all hover:scale-[1.01] hover:opacity-95"
									>
										Começar teste grátis
									</button>
								</div>
							</div>
						</div>
					</div>
				</section>

				<section className="border-b border-brand-orange/10 bg-white py-24 text-center">
					<div className="mx-auto max-w-5xl space-y-14 px-6">
						<div className="space-y-8">
							<h2 className="text-3xl font-extrabold text-[#3d3431] md:text-4xl">
								Seguro, ético e{' '}
								<span className="font-bold text-brand-orange">
									em conformidade.
								</span>
							</h2>
							<p className="mx-auto max-w-xl font-light leading-relaxed text-gray-500">
								Prontuário digital em conformidade com a LGPD e as
								resoluções do CFP. Seus dados e os de seus pacientes
								protegidos com criptografia.
							</p>

							<div className="flex flex-wrap items-center justify-center gap-3">
								{TRUST_ITEMS.map((item) => {
									const { Icon } = item;
									return (
										<div
											key={item.id}
											className={cn(
												'inline-flex items-center gap-2.5 rounded-xl border bg-[#fffcfb] px-4 py-2.5 transition-all hover:shadow-sm',
												trustAccentClasses[item.accent],
											)}
										>
											<Icon className="h-4 w-4 shrink-0" />
											<span className="text-xs font-bold uppercase tracking-wider text-[#3d3431]">
												{item.title}
											</span>
										</div>
									);
								})}
							</div>
						</div>

						<div className="space-y-6">
							<h3 className="text-sm font-bold uppercase tracking-[0.18em] text-brand-orange">
								Parcerias e integrações
							</h3>
							<div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-6 sm:gap-x-10">
								{PARTNER_INTEGRATIONS.map((partner) => (
									<div
										key={partner.name}
										className="flex h-8 w-[7.25rem] shrink-0 items-center justify-center"
										title={partner.name}
									>
										<img
											src={partner.logo}
											alt={partner.name}
											className="max-h-full max-w-full object-contain object-center opacity-80 transition hover:opacity-100"
											style={
												partner.logoScale
													? {
															transform: `scale(${partner.logoScale})`,
														}
													: undefined
											}
											loading="lazy"
										/>
									</div>
								))}
							</div>
						</div>

						<ReclameAquiVerifiedSection variant="embedded" />
					</div>
				</section>

				<section
					id="cta-final"
					className="relative overflow-hidden bg-brand-orange py-32 text-center"
				>
					<div className="pointer-events-none absolute inset-0 z-0 hidden lg:block">
						<svg
							className="h-full w-full"
							viewBox="0 0 1440 400"
							preserveAspectRatio="none"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M-50,220 C150,220 300,350 600,350 C850,350 900,100 1100,100 C1250,100 1200,320 1120,320 C1050,320 1080,180 1250,180 C1450,180 1550,100 1650,100"
								stroke="rgba(255, 255, 255, 0.55)"
								strokeWidth="3"
								strokeLinecap="round"
								style={DASHED_PATH_STYLE}
							/>
						</svg>
					</div>

					<div className="relative z-10 mx-auto max-w-[700px] space-y-8 px-6">
						<h2 className="text-4xl font-extrabold leading-tight text-white md:text-6xl">
							Comece seu teste
							<br />
							grátis de 7 dias.
						</h2>
						<p className="mx-auto max-w-md text-lg font-light leading-relaxed text-white/90">
							Cadastre-se agora e ganhe tempo na sua rotina com
							agenda, IA e videoconferência em um só lugar.
						</p>
						<button
							type="button"
							onClick={goToRegister}
							className="relative z-10 cursor-pointer rounded-2xl bg-white px-12 py-5 text-xl font-bold text-brand-orange shadow-xl transition-all hover:shadow-2xl active:scale-95"
						>
							Criar conta gratuita
						</button>
					</div>
				</section>

				<PsychologistLandingFaqSection />

				<section className="border-b border-brand-orange/10 bg-gradient-to-r from-brand-orange/10 to-brand-pink/10 py-16">
					<div className="mx-auto flex max-w-4xl flex-col items-center justify-between gap-6 px-6 text-center md:flex-row md:text-left">
						<div>
							<h3 className="text-2xl font-bold text-[#3d3431]">
								Prefere falar com alguém?
							</h3>
							<p className="mt-2 text-sm font-light text-gray-500">
								Nosso time responde dúvidas sobre a Plumaa para
								psicólogos pelo WhatsApp.
							</p>
						</div>
						<a
							href={WHATSAPP_LINK_P2P}
							target="_blank"
							rel="noopener noreferrer"
							className="shrink-0 cursor-pointer rounded-2xl bg-gradient-to-r from-brand-orange to-brand-pink px-8 py-4 text-sm font-bold text-white shadow-lg transition-all hover:scale-[1.02]"
						>
							Falar pelo WhatsApp
						</a>
					</div>
				</section>

				<footer className="relative overflow-hidden border-t-2 border-brand-blue/30 bg-[#1a1210] pb-12 pt-20 text-[#e8e2df]">
					<div className="pointer-events-none absolute left-1/4 top-0 h-28 w-96 -translate-y-1/2 rounded-full bg-brand-blue/10 blur-3xl" />

					<div className="relative z-10 mx-auto max-w-6xl px-6">
						<div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-12">
							<div className="space-y-6 md:col-span-4">
								<img
									src={PLUMAA_LOGO_WHITE}
									alt="Plumaa"
									className="h-9 w-auto md:h-10"
								/>
								<p className="max-w-sm text-sm font-light leading-relaxed text-[#e8e2df]/70">
									Consultório digital para psicólogos:
									agenda, videoconferência, prontuário com IA e
									muito mais em uma única plataforma.
								</p>
							</div>

							<div className="space-y-5 md:col-span-3">
								<h6 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-pink">
									<Link
										to="/blog"
										className="transition-colors hover:text-brand-pink/80"
									>
										Blog Plumaa
									</Link>
								</h6>
								<div className="flex flex-col space-y-3.5 text-sm font-light">
									{blogData?.data.map((post) => (
										<Link
											key={post.id}
											to={`/blog/${encodeURIComponent(
												post.slug,
											)}`}
											className="flex items-start gap-2 leading-snug text-[#e8e2df]/70 transition-colors hover:text-[#e8e2df]"
										>
											<span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-brand-pink" />
											{post.title}
										</Link>
									))}
									{(!blogData ||
										blogData.data.length === 0) && (
										<Link
											to="/blog"
											className="text-[#e8e2df]/70 transition-colors hover:text-[#e8e2df]"
										>
											Ver todos os artigos
										</Link>
									)}
								</div>
							</div>

							<div className="space-y-5 md:col-span-2">
								<h6 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-orange">
									Navegação
								</h6>
								<div className="flex flex-col space-y-3.5 text-sm font-light">
									<button
										type="button"
										onClick={scrollToPricing}
										className="flex cursor-pointer items-center gap-1.5 text-left text-[#e8e2df] transition-colors hover:text-brand-blue"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-brand-orange" />
										Plano e preços
									</button>
									<a
										href="#como-funciona"
										className="flex items-center gap-1.5 text-[#e8e2df] transition-colors hover:text-brand-blue"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-brand-pink" />
										Como funciona
									</a>
									<a
										href="#central-ajuda"
										className="flex items-center gap-1.5 text-[#e8e2df] transition-colors hover:text-brand-blue"
									>
										<span className="h-1.5 w-1.5 rounded-full bg-brand-blue" />
										Central de ajuda
									</a>
								</div>
							</div>

							<div className="space-y-5 md:col-span-3">
								<h6 className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-blue">
									Contato
								</h6>
								<div className="space-y-4 text-sm font-light text-[#e8e2df]/80">
									<p className="flex items-center gap-3">
										<Phone className="h-4 w-4 shrink-0 text-brand-blue" />
										<span>{WHATSAPP_NUMBER_FORMATTED}</span>
									</p>
									<p className="flex items-center gap-3">
										<Mail className="h-4 w-4 shrink-0 text-brand-blue" />
										<span>contato@plumaa.com.br</span>
									</p>
									<Link
										to="/termos-de-uso-do-profissional"
										className="block text-[#e8e2df]/70 transition-colors hover:text-[#e8e2df]"
									>
										Termos do profissional
									</Link>
									<Link
										to="/politica-de-privacidade"
										className="block text-[#e8e2df]/70 transition-colors hover:text-[#e8e2df]"
									>
										Política de privacidade
									</Link>
								</div>
							</div>
						</div>

						<div className="border-t border-[#e8e2df]/10 pt-8 text-center text-xs font-light text-[#e8e2df]/50">
							© {new Date().getFullYear()} Plumaa. Todos os
							direitos reservados.
						</div>
					</div>
				</footer>
			</div>

			<MediaLightbox
				isOpen={
					lightboxMedia !== null || expandedAnimatedDemo !== null
				}
				onClose={() => {
					setLightboxMedia(null);
					setExpandedAnimatedDemo(null);
				}}
				src={lightboxMedia?.src ?? ''}
				alt={
					lightboxMedia?.alt ?? expandedAnimatedDemo?.alt ?? undefined
				}
			>
				{expandedAnimatedDemo ? (
					<WorkflowDemoPlayer
						demoId={expandedAnimatedDemo.demoId}
						alt={expandedAnimatedDemo.alt}
						expanded
						className="w-[min(90vw,960px)]"
					/>
				) : null}
			</MediaLightbox>
		</>
	);
}
